import { defineStore } from 'pinia';
import { addDoc, collection, deleteDoc, doc, getDocs, query, updateDoc, where } from 'firebase/firestore';
import { generateSlug, makeSlugUnique } from '~/lib/slugify';
import { STARTER_TEMPLATES, STARTER_WORKFLOWS } from '~/lib/projectWorkflowDefaults';
import { resolveWorkspaceEntitlements } from '~/lib/subscriptionPlans';
import type {
  ProjectTemplate,
  ProjectTemplateQuoteDefaults,
  ProjectWorkflow,
  QuoteInstallment,
  QuoteLineItem,
  WorkflowMilestoneTemplate,
  WorkflowTaskTemplate,
} from '~/types/models';

function stripUndefined<T>(value: T): T {
  if (Array.isArray(value)) {
    return value
      .map((entry) => stripUndefined(entry))
      .filter((entry) => entry !== undefined) as T;
  }

  if (value instanceof Date || value === null || typeof value !== 'object') {
    return value;
  }

  return Object.fromEntries(
    Object.entries(value)
      .map(([key, entry]) => [key, stripUndefined(entry)])
      .filter(([, entry]) => entry !== undefined),
  ) as T;
}

function cloneMilestones(milestones: WorkflowMilestoneTemplate[]): WorkflowMilestoneTemplate[] {
  return milestones.map((milestone, index) => stripUndefined({
    ...milestone,
    id: milestone.id || `milestone-${index + 1}`,
    name: String(milestone.name || '').trim(),
    description: milestone.description ? String(milestone.description).trim() : undefined,
    order: index + 1,
    requiresClientApproval: milestone.requiresClientApproval === true,
  }));
}

function cloneTasks(tasks: WorkflowTaskTemplate[]): WorkflowTaskTemplate[] {
  const normalized = tasks.map((task, index) => stripUndefined({
    ...task,
    id: task.id || `task-template-${index + 1}`,
    title: String(task.title || '').trim(),
    description: task.description ? String(task.description).trim() : undefined,
    milestoneId: task.milestoneId || undefined,
    parentTaskTemplateId: task.parentTaskTemplateId || undefined,
    priority: task.priority || undefined,
    dueOffsetDays: task.dueOffsetDays !== undefined && task.dueOffsetDays !== null
      ? Number(task.dueOffsetDays)
      : undefined,
    clientVisible: task.clientVisible === true,
  }));
  const taskIds = new Set(normalized.map(task => task.id));
  return normalized.map(task => ({
    ...task,
    parentTaskTemplateId: task.parentTaskTemplateId
      && task.parentTaskTemplateId !== task.id
      && taskIds.has(task.parentTaskTemplateId)
      ? task.parentTaskTemplateId
      : undefined,
  }));
}

function cloneQuoteLineItems(items: QuoteLineItem[] = []): QuoteLineItem[] {
  return items.map((item) => stripUndefined({
    name: String(item.name || '').trim(),
    description: item.description ? String(item.description) : '',
    quantity: Number(item.quantity || 0),
    price: Number(item.price || 0),
    discount: Number(item.discount || 0),
  }));
}

function cloneInstallments(installments: QuoteInstallment[] = []): QuoteInstallment[] {
  return installments.map((installment) => stripUndefined({
    label: String(installment.label || '').trim(),
    dueDate: installment.dueDate || undefined,
    amount: Number(installment.amount || 0),
    percentage: installment.percentage !== undefined ? Number(installment.percentage || 0) : undefined,
    status: installment.status || 'pending',
  }));
}

function cloneQuoteDefaults(quoteDefaults?: ProjectTemplateQuoteDefaults): ProjectTemplateQuoteDefaults | undefined {
  if (!quoteDefaults) return undefined;

  return stripUndefined({
    enabled: quoteDefaults.enabled === true,
    autoCreate: quoteDefaults.autoCreate === true,
    title: quoteDefaults.title || '',
    currency: quoteDefaults.currency || undefined,
    issueDateOffsetDays: quoteDefaults.issueDateOffsetDays !== undefined ? Number(quoteDefaults.issueDateOffsetDays || 0) : undefined,
    expiryDateOffsetDays: quoteDefaults.expiryDateOffsetDays !== undefined ? Number(quoteDefaults.expiryDateOffsetDays || 0) : undefined,
    lineItems: cloneQuoteLineItems(quoteDefaults.lineItems || []),
    deposit: quoteDefaults.deposit
      ? {
          enabled: quoteDefaults.deposit.enabled === true,
          mode: quoteDefaults.deposit.mode || 'fixed',
          value: Number(quoteDefaults.deposit.value || 0),
        }
      : undefined,
    paymentPlan: quoteDefaults.paymentPlan?.enabled
      ? {
          enabled: true,
          installments: cloneInstallments(quoteDefaults.paymentPlan.installments || []),
        }
      : undefined,
  });
}

export const useWorkflowStore = defineStore('workflows', {
  state: () => ({
    workflows: [] as ProjectWorkflow[],
    templates: [] as ProjectTemplate[],
    loading: false,
    error: null as string | null,
  }),

  actions: {
    _getTenantId(): string {
      const tenantStore = useTenantStore();
      if (!tenantStore.currentTenantId) {
        throw new Error('No tenant selected. Please log in.');
      }
      return tenantStore.currentTenantId;
    },

    _workflowsCollection() {
      const { $db } = useNuxtApp();
      return collection($db, `tenants/${this._getTenantId()}/workflows`);
    },

    _templatesCollection() {
      const { $db } = useNuxtApp();
      return collection($db, `tenants/${this._getTenantId()}/projectTemplates`);
    },

    _normalizeWorkflow(workflow: ProjectWorkflow): ProjectWorkflow {
      return {
        ...workflow,
        milestones: cloneMilestones(workflow.milestones || []),
        tasks: cloneTasks(workflow.tasks || []),
      };
    },

    _normalizeTemplate(template: ProjectTemplate): ProjectTemplate {
      return {
        ...template,
        milestones: cloneMilestones(template.milestones || []),
        tasks: cloneTasks(template.tasks || []),
        quoteDefaults: cloneQuoteDefaults(template.quoteDefaults),
      };
    },

    _assertWorkflowAccess() {
      const tenantStore = useTenantStore();
      const entitlements = resolveWorkspaceEntitlements(tenantStore.currentTenant);
      if (!entitlements.plan.features.workflows || !entitlements.plan.features.projectTemplates) {
        throw new Error('Workflows and project templates are available on the Growth plan and above.');
      }
    },

    async fetchWorkflows() {
      this.loading = true;
      this.error = null;

      try {
        const snapshot = await getDocs(this._workflowsCollection());
        this.workflows = snapshot.docs
          .map((entry) => this._normalizeWorkflow({ id: entry.id, ...entry.data() } as ProjectWorkflow))
          .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
      } catch (error: any) {
        this.error = error.message || 'Failed to load workflows.';
        console.error('Error fetching workflows:', error);
      } finally {
        this.loading = false;
      }
    },

    async fetchTemplates() {
      this.loading = true;
      this.error = null;

      try {
        const snapshot = await getDocs(this._templatesCollection());
        this.templates = snapshot.docs
          .map((entry) => this._normalizeTemplate({ id: entry.id, ...entry.data() } as ProjectTemplate))
          .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
      } catch (error: any) {
        this.error = error.message || 'Failed to load templates.';
        console.error('Error fetching templates:', error);
      } finally {
        this.loading = false;
      }
    },

    async fetchAll() {
      await Promise.all([this.fetchWorkflows(), this.fetchTemplates()]);
    },

    async addWorkflow(payload: Omit<ProjectWorkflow, 'id' | 'slug' | 'tenantId' | 'createdAt' | 'updatedAt'>) {
      this._assertWorkflowAccess();
      const tenantId = this._getTenantId();
      const slug = makeSlugUnique(generateSlug(payload.name || 'workflow'));
      const document = stripUndefined({
        ...payload,
        tenantId,
        slug,
        milestones: cloneMilestones(payload.milestones || []),
        tasks: cloneTasks(payload.tasks || []),
        quoteDefaults: cloneQuoteDefaults(payload.quoteDefaults),
        createdAt: new Date(),
        updatedAt: new Date(),
      });

      const docRef = await addDoc(this._workflowsCollection(), document);
      this.workflows.push(this._normalizeWorkflow({ id: docRef.id, ...document } as ProjectWorkflow));
      this.workflows.sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
      return docRef.id;
    },

    async updateWorkflow(workflowId: string, payload: Partial<ProjectWorkflow>) {
      this._assertWorkflowAccess();
      const { $db } = useNuxtApp();
      const tenantId = this._getTenantId();
      const docRef = doc($db, `tenants/${tenantId}/workflows`, workflowId);
      const normalized = stripUndefined({
        ...payload,
        ...(payload.milestones ? { milestones: cloneMilestones(payload.milestones) } : {}),
        ...(payload.tasks ? { tasks: cloneTasks(payload.tasks) } : {}),
        ...(payload.quoteDefaults ? { quoteDefaults: cloneQuoteDefaults(payload.quoteDefaults) } : {}),
        updatedAt: new Date(),
      });

      await updateDoc(docRef, normalized);
      const index = this.workflows.findIndex((workflow) => workflow.id === workflowId);
      if (index !== -1) {
        this.workflows[index] = this._normalizeWorkflow({ ...this.workflows[index], ...normalized } as ProjectWorkflow);
      }
    },

    async deleteWorkflow(workflowId: string) {
      this._assertWorkflowAccess();
      const { $db } = useNuxtApp();
      const tenantId = this._getTenantId();
      await deleteDoc(doc($db, `tenants/${tenantId}/workflows`, workflowId));
      this.workflows = this.workflows.filter((workflow) => workflow.id !== workflowId);
    },

    async addTemplate(payload: Omit<ProjectTemplate, 'id' | 'slug' | 'tenantId' | 'createdAt' | 'updatedAt'>) {
      this._assertWorkflowAccess();
      const tenantId = this._getTenantId();
      const slug = makeSlugUnique(generateSlug(payload.name || 'template'));
      const document = stripUndefined({
        ...payload,
        tenantId,
        slug,
        milestones: cloneMilestones(payload.milestones || []),
        tasks: cloneTasks(payload.tasks || []),
        createdAt: new Date(),
        updatedAt: new Date(),
      });

      const docRef = await addDoc(this._templatesCollection(), document);
      this.templates.push(this._normalizeTemplate({ id: docRef.id, ...document } as ProjectTemplate));
      this.templates.sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
      return docRef.id;
    },

    async updateTemplate(templateId: string, payload: Partial<ProjectTemplate>) {
      this._assertWorkflowAccess();
      const { $db } = useNuxtApp();
      const tenantId = this._getTenantId();
      const docRef = doc($db, `tenants/${tenantId}/projectTemplates`, templateId);
      const normalized = stripUndefined({
        ...payload,
        ...(payload.milestones ? { milestones: cloneMilestones(payload.milestones) } : {}),
        ...(payload.tasks ? { tasks: cloneTasks(payload.tasks) } : {}),
        updatedAt: new Date(),
      });

      await updateDoc(docRef, normalized);
      const index = this.templates.findIndex((template) => template.id === templateId);
      if (index !== -1) {
        this.templates[index] = this._normalizeTemplate({ ...this.templates[index], ...normalized } as ProjectTemplate);
      }
    },

    async deleteTemplate(templateId: string) {
      this._assertWorkflowAccess();
      const { $db } = useNuxtApp();
      const tenantId = this._getTenantId();
      await deleteDoc(doc($db, `tenants/${tenantId}/projectTemplates`, templateId));
      this.templates = this.templates.filter((template) => template.id !== templateId);
    },

    async seedStarterWorkflows(userId: string) {
      this._assertWorkflowAccess();
      const existingNames = new Set(this.workflows.map((workflow) => workflow.name));

      for (const starter of STARTER_WORKFLOWS) {
        if (existingNames.has(starter.name)) continue;
        await this.addWorkflow({
          ...starter,
          createdBy: userId,
          updatedBy: userId,
        });
      }
    },

    async seedStarterTemplates(userId: string) {
      this._assertWorkflowAccess();
      const workflowLookup = new Map(this.workflows.map((workflow) => [workflow.name, workflow]));
      const existingNames = new Set(this.templates.map((template) => template.name));

      for (const starter of STARTER_TEMPLATES) {
        if (existingNames.has(starter.name)) continue;

        const linkedWorkflow = starter.workflowName ? workflowLookup.get(starter.workflowName) : undefined;

        await this.addTemplate({
          ...starter,
          workflowId: linkedWorkflow?.id,
          workflowName: linkedWorkflow?.name || starter.workflowName,
          createdBy: userId,
          updatedBy: userId,
        });
      }
    },

    async seedStarters(userId: string) {
      this._assertWorkflowAccess();
      await this.seedStarterWorkflows(userId);
      await this.fetchWorkflows();
      await this.seedStarterTemplates(userId);
      await this.fetchTemplates();
    },

    getWorkflowById(workflowId?: string) {
      return workflowId ? this.workflows.find((workflow) => workflow.id === workflowId) || null : null;
    },

    getTemplateById(templateId?: string) {
      return templateId ? this.templates.find((template) => template.id === templateId) || null : null;
    },
  },
});
